import { alpha, Box, Stack } from "@mui/material";
import { type SupportArticle } from "@src/appV2/HelpCenter/types";
import { useEffect, useState } from "react";

import { Icon } from "../../components/Icon";
import { SearchInput } from "../../components/SearchInput/SearchInput";
import { Text } from "../../components/Text";
import { applyPlatformAwareInteractionStyles } from "../../utils/applyPlatformAwareInteractionStyles";
import { ALL_SUPPORT_ARTICLES_CATEGORY } from "../constants";
import {
  getBackgroundMuted,
  getBackgroundStandout,
  getBorderBase,
  getMutedText,
  getStrongText,
} from "../themeHelpers";

interface HelpCenterSearchFieldProps {
  isMobile: boolean;
  searchQuery: string;
  shouldShowSuggestions: boolean;
  suggestedSupportArticles: SupportArticle[];
  onSearchChange: (searchQuery: string) => void;
  onSelectCategory: (selectedCategory: string) => void;
}

export function HelpCenterSearchField(props: HelpCenterSearchFieldProps) {
  const {
    isMobile,
    searchQuery,
    shouldShowSuggestions,
    suggestedSupportArticles,
    onSearchChange,
    onSelectCategory,
  } = props;

  const [isSuggestionsDismissed, setIsSuggestionsDismissed] = useState(false);
  const showSuggestions = shouldShowSuggestions && !isSuggestionsDismissed;

  useEffect(() => {
    if (!showSuggestions) {
      return;
    }

    const dismiss = () => {
      setIsSuggestionsDismissed(true);
    };

    window.addEventListener("scroll", dismiss, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", dismiss, { capture: true });
    };
  }, [showSuggestions]);

  return (
    <Box
      sx={{ position: "relative", zIndex: 2 }}
      onBlur={(event: React.FocusEvent) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setIsSuggestionsDismissed(true);
        }
      }}
      onFocus={() => {
        setIsSuggestionsDismissed(false);
      }}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
          setIsSuggestionsDismissed(true);
        } else {
          setIsSuggestionsDismissed(false);
        }
      }}
    >
      <SearchInput
        debounceMs={150}
        placeholder="Search for articles..."
        sx={(theme) => ({
          "& .MuiOutlinedInput-root": {
            borderRadius: theme.borderRadius?.xxxLarge,
            minHeight: isMobile ? "3.5rem" : "4.25rem",
            backgroundColor: theme.palette.background.paper,
            boxShadow: isMobile
              ? `0 0.875rem 2rem ${alpha(getStrongText(theme), 0.08)}`
              : `0 0.5rem 1.25rem ${alpha(getStrongText(theme), 0.04)}`,
            transition: theme.transitions.create(
              ["box-shadow", "background-color", "border-color"],
              { duration: theme.transitions.duration.short }
            ),
            ...applyPlatformAwareInteractionStyles({
              hoverStyle: {
                backgroundColor: theme.palette.background.paper,
                boxShadow: isMobile
                  ? `0 1rem 2.25rem ${alpha(getStrongText(theme), 0.1)}`
                  : `0 0.75rem 1.5rem ${alpha(getStrongText(theme), 0.06)}`,
              },
            }),
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: getBorderBase(theme),
            borderWidth: 1,
          },
          "@media (hover: hover)": {
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: getBorderBase(theme),
              borderWidth: 1,
            },
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(getStrongText(theme), 0.22),
            borderWidth: 2,
          },
          "& .MuiOutlinedInput-input": {
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            fontSize: theme.typography.body1.fontSize,
            color: getStrongText(theme),
            "&::placeholder": {
              color: getMutedText(theme),
              opacity: 1,
            },
          },
          "& .MuiInputAdornment-root svg": {
            color: getMutedText(theme),
          },
        })}
        value={searchQuery}
        onChange={onSearchChange}
      />

      {showSuggestions ? (
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 10,
            marginTop: theme.spacing(1),
          })}
        >
          <Stack
            role="listbox"
            sx={(theme) => ({
              gap: "0.25rem",
              padding: theme.spacing(1),
              borderRadius: theme.borderRadius?.large,
              border: `1px solid ${getBorderBase(theme)}`,
              backgroundColor: getBackgroundStandout(theme),
              boxShadow: `0 0.875rem 2rem ${alpha(getStrongText(theme), 0.1)}`,
            })}
          >
            {suggestedSupportArticles.map((supportArticle) => (
              <Box
                key={supportArticle.id}
                aria-label={supportArticle.attributes.title}
                component="button"
                type="button"
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: theme.spacing(2),
                  width: "100%",
                  padding: "0.75rem 1rem",
                  borderRadius: theme.borderRadius?.large,
                  border: "none",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: theme.transitions.create(["background-color"], {
                    duration: theme.transitions.duration.shorter,
                  }),
                  ...applyPlatformAwareInteractionStyles({
                    hoverStyle: {
                      backgroundColor: getBackgroundMuted(theme),
                    },
                  }),
                })}
                onClick={() => {
                  setIsSuggestionsDismissed(false);
                  onSearchChange(supportArticle.attributes.title);
                  onSelectCategory(ALL_SUPPORT_ARTICLES_CATEGORY);
                }}
              >
                <Stack sx={{ minWidth: 0, gap: "0.125rem" }}>
                  <Text semibold variant="body2">
                    {supportArticle.attributes.title}
                  </Text>
                  <Text variant="caption" sx={(theme) => ({ color: getMutedText(theme) })}>
                    {supportArticle.attributes.category}
                  </Text>
                </Stack>

                <Icon
                  size="small"
                  sx={(theme) => ({ color: getMutedText(theme) })}
                  type="chevron-right"
                />
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}
